import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Loader2, AlertTriangle, Mail, LogIn } from "lucide-react";
import { PageHero } from "../components/shared/Primitives";
import SEOHead from "../components/shared/SEOHead";
import { api, formatApiError, API } from "../lib/api";

export default function ShopProduct() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null); // null = loading, false = not found
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [recoverEmail, setRecoverEmail] = useState("");
  const [recoverStatus, setRecoverStatus] = useState({ loading: false, message: "" });

  useEffect(() => {
    let alive = true;
    api
      .get(`/api/shop/products/${slug}`)
      .then(({ data }) => {
        if (alive) setProduct(data);
      })
      .catch(() => {
        if (alive) setProduct(false);
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  useEffect(() => {
    if (searchParams.get("checkout") === "cancelled") {
      toast.info("Checkout cancelled — no payment was taken.", { id: "shop-checkout-cancelled" });
      navigate(`/shop/${slug}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const onBuy = async () => {
    setCheckoutLoading(true);
    setCheckoutError("");
    try {
      const { data } = await api.post("/api/shop/checkout", { slug, origin_url: window.location.origin });
      window.location.assign(data.checkout_url);
    } catch (err) {
      setCheckoutError(formatApiError(err.response?.data?.detail) || err.message);
      setCheckoutLoading(false);
    }
  };

  const onBuyService = async () => {
    setCheckoutLoading(true);
    setCheckoutError("");
    try {
      const { data } = await api.post("/api/shop/service-checkout", { slug, origin_url: window.location.origin });
      if (data.checkout_url) {
        window.location.assign(data.checkout_url);
      } else {
        navigate(`/shop/success?session_id=${data.session_id}`);
      }
    } catch (err) {
      setCheckoutError(formatApiError(err.response?.data?.detail) || err.message);
      setCheckoutLoading(false);
    }
  };

  const onRecover = async (e) => {
    e.preventDefault();
    setRecoverStatus({ loading: true, message: "" });
    try {
      const { data } = await api.post("/api/shop/recover-download", { email: recoverEmail, slug });
      setRecoverStatus({ loading: false, message: data.message });
    } catch (err) {
      setRecoverStatus({ loading: false, message: formatApiError(err.response?.data?.detail) || err.message });
    }
  };

  if (product === null) {
    return (
      <div className="container-jcc py-24 text-center text-ink-muted" data-testid="shop-product-loading">
        Loading…
      </div>
    );
  }
  if (product === false) {
    return (
      <div className="container-jcc py-24 text-center" data-testid="shop-product-not-found">
        <h1 className="font-display text-2xl font-bold">We couldn't find this product</h1>
        <Link to="/shop" className="btn-teal mt-6 inline-flex">
          Back to JCC Shop
        </Link>
      </div>
    );
  }

  const isService = product.product_type === "service";
  const previews = Array.from({ length: product.preview_count });

  return (
    <div data-testid="page-shop-product">
      <SEOHead title={product.title} description={product.subtitle} path={`/shop/${product.slug}`} />
      <PageHero eyebrow="JCC Shop" title={product.title} description={product.subtitle} accent="teal" />

      <section className="section">
        <div className="container-jcc grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3 space-y-8">
            {previews.length > 0 && (
              <div data-testid="shop-product-previews">
                <p className="overline text-teal mb-3">Sample preview</p>
                <div className="grid grid-cols-3 gap-3">
                  {previews.map((_, i) => (
                    <img
                      key={i}
                      src={`${API}/shop/products/${product.slug}/preview/${i}`}
                      alt={`${product.title} sample card ${i + 1}`}
                      className="rounded-xl border border-slate-200 w-full object-cover aspect-[3/4]"
                      data-testid={`shop-preview-image-${i}`}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-4 text-ink-muted leading-relaxed">
              {product.description.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            {product.card_topics.length > 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="shop-card-topics">
                <p className="text-sm font-semibold text-ink mb-3">The ten cards cover:</p>
                <div className="flex flex-wrap gap-2">
                  {product.card_topics.map((t) => (
                    <span key={t} className="pill bg-teal/10 text-teal">
                      {t}
                    </span>
                  ))}
                </div>
                {product.card_structure_note && (
                  <p className="mt-4 text-sm text-ink-muted">{product.card_structure_note}</p>
                )}
              </div>
            )}

            {product.product_info_bullets.length > 0 && (
              <div data-testid="shop-product-info">
                <p className="text-sm font-semibold text-ink mb-3">Product information</p>
                <ul className="space-y-2">
                  {product.product_info_bullets.map((b) => (
                    <li key={b} className="flex gap-2.5 text-sm text-ink-muted">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-teal" strokeWidth={1.75} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {isService && product.turnaround_time && (
              <div className="rounded-2xl border border-teal/20 bg-teal/5 p-5 text-sm text-ink" data-testid="shop-turnaround-time">
                <strong>Turnaround time:</strong> {product.turnaround_time}
              </div>
            )}
            {isService && product.return_instructions && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-ink-muted whitespace-pre-line" data-testid="shop-return-instructions">
                {product.return_instructions}
              </div>
            )}

            {product.participation_note && (
              <div
                className="rounded-2xl border border-purple/20 bg-purple/5 p-5 text-sm text-ink-muted"
                data-testid="shop-participation-note"
              >
                {product.participation_note}
              </div>
            )}

            {product.usage_terms && (
              <div
                id="usage-terms"
                className="scroll-mt-28 rounded-2xl border border-slate-200 bg-slate-50 p-5"
                data-testid="shop-usage-terms"
              >
                <p className="text-sm font-semibold text-ink mb-2">Usage terms</p>
                <p className="text-sm text-ink-muted leading-relaxed">{product.usage_terms}</p>
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <div className="card-base p-6 lg:sticky lg:top-24" data-testid="shop-buy-box">
              <p className="text-sm text-ink-muted">{product.format_label}</p>
              <p className="mt-2 font-display font-bold text-3xl text-teal" data-testid="shop-product-price">
                {isService ? product.your_price_display : product.price_display}
              </p>
              {isService && product.is_member_price && (
                <p className="text-xs text-purple font-medium" data-testid="shop-member-price-applied">Pro Carer Office member price</p>
              )}
              {isService && !product.is_member_price && product.member_price_display && (
                <p className="text-xs text-ink-soft" data-testid="shop-member-price-teaser">
                  Pro Carer Office members pay {product.member_price_display}
                </p>
              )}
              <p className="text-sm text-ink-soft">{isService ? "Personalised service" : "One-off purchase, no membership required"}</p>

              {!product.on_sale && (
                <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-ink-muted" data-testid="shop-not-on-sale">
                  This product is not currently available for purchase.
                </div>
              )}

              {product.on_sale && !isService && (
                <>
                  <button onClick={onBuy} disabled={checkoutLoading} className="btn-teal w-full mt-5 justify-center disabled:opacity-60" data-testid="shop-buy-btn">
                    {checkoutLoading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to secure checkout…</>) : (`Buy PDF — ${product.price_display}`)}
                  </button>
                  {checkoutError && (
                    <p className="mt-3 text-sm text-red-700 flex gap-2" data-testid="shop-checkout-error">
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                      {checkoutError}
                    </p>
                  )}
                  <p className="mt-3 text-xs text-ink-soft flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5" /> Secure checkout via Stripe. Instant digital delivery by email.
                  </p>
                </>
              )}

              {product.on_sale && isService && !product.signed_in_email && (
                <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm" data-testid="shop-service-login-required">
                  <p className="text-ink-muted mb-3">Please sign in with your JCC account to order this service.</p>
                  <Link to={`/auth/handover/request?next=${encodeURIComponent(`/shop/${slug}`)}`} className="btn-teal w-full justify-center" data-testid="shop-service-login-link">
                    <LogIn className="w-4 h-4" /> Log in to continue
                  </Link>
                  <Link to={`/join/carer-starter?next=${encodeURIComponent(`/shop/${slug}`)}`} className="btn-outline w-full justify-center mt-2 text-sm" data-testid="shop-service-join-link">
                    New carer? Create a free account
                  </Link>
                </div>
              )}

              {product.on_sale && isService && product.signed_in_email && (
                <div className="mt-5 space-y-3" data-testid="shop-service-buy-box">
                  <p className="text-xs text-ink-soft">
                    After checkout you'll get a secure link to complete a short questionnaire — you can save your
                    progress and finish it later.
                  </p>
                  <button onClick={onBuyService} disabled={checkoutLoading}
                    className="btn-teal w-full justify-center disabled:opacity-60" data-testid="shop-buy-service-btn">
                    {checkoutLoading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Processing…</>) : (`Order — ${product.your_price_display}`)}
                  </button>
                  {checkoutError && (
                    <p className="text-sm text-red-700 flex gap-2" data-testid="shop-checkout-error">
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                      {checkoutError}
                    </p>
                  )}
                </div>
              )}

              {!isService && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <p className="text-sm font-medium text-ink">Already bought this?</p>
                  <p className="mt-1 text-xs text-ink-muted">
                    Lost your download email or link expired? We'll send a fresh one.
                  </p>
                  <form onSubmit={onRecover} className="mt-3 flex flex-col gap-2">
                    <input
                      type="email"
                      required
                      value={recoverEmail}
                      onChange={(e) => setRecoverEmail(e.target.value)}
                      placeholder="you@example.com"
                      data-testid="shop-recover-email-input"
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900"
                    />
                    <button
                      type="submit"
                      disabled={recoverStatus.loading}
                      className="btn-outline text-sm justify-center"
                      data-testid="shop-recover-submit-btn"
                    >
                      <Mail className="w-4 h-4" /> {recoverStatus.loading ? "Sending…" : "Resend my download link"}
                    </button>
                  </form>
                  {recoverStatus.message && (
                    <p className="mt-2 text-xs text-ink-muted" data-testid="shop-recover-message">
                      {recoverStatus.message}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
